import {
    SafetyOutlined,
    TeamOutlined,
    UnlockOutlined,
    StopOutlined,
    LockOutlined
} from '@ant-design/icons';
import userEligibilityFormStyles from './elements/userEligibilityForm/styles/userEligibilityForm.module.scss';

export const TAB_SEARCH_PARAM_KEY = 'tab';
export const SUCCESS_TAB_KEY = 'success';

/** Flip when `updateFI` accepts `access_rules` / `access_enabled` in `fi_info`. */
export const ACCESS_RULES_PERSIST_ENABLED = false;

export const tabs = [
    {
        key: '0',
        label: 'User eligibility',
        icon: null,
    },
    {
        key: '1',
        label: 'Available balance',
        icon: null,
    },
    {
        key: '2',
        label: 'Fee structure',
        icon: null,
    },
    {
        key: '3',
        label: 'Limits',
        icon: null,
    }
];

export const CRITERIA_OPERATOR_OPTIONS = [
    { value: 'lt', label: 'Less than' },
    { value: 'lte', label: 'Less or equal to ' },
    { value: 'eq', label: 'Equal' },
    { value: 'gte', label: 'Greater or equal to' },
    { value: 'gt', label: 'More than' },
];

// Declarative defs driving both the restricted-criteria panel render and default-state shape.
// `windowed: true` criteria render the extra "In the last [N] days" control.
export const RESTRICTED_CRITERIA_DEFS = [
    {
        key: 'tenure',
        title: 'Tenure at your financial institution',
        description: 'The amount of time a user has been banking at your institution.',
        unit: 'days',
        windowed: false
    },
    {
        key: 'overdraft_instances',
        title: 'Overdraft Instances',
        description: 'The amount of overdrafts a user has done in your preferred time window.',
        unit: 'overdrafts',
        windowed: true
    },
    {
        key: 'nsf_instances',
        title: 'NSF Instances',
        description: 'The amount of NSFs a user has done in your preferred time window.',
        unit: 'NSFs',
        windowed: true
    },
    {
        key: 'negative_balance',
        title: 'Negative Balance',
        description: 'Consecutive days a user has their account balance negative.',
        unit: 'days consecutively overdrawn',
        windowed: true
    },
    {
        key: 'employer_tenure',
        title: 'Employer Tenure',
        description: 'The amount of time a user has been employed.',
        unit: 'days employed at their current job',
        windowed: false
    }
];

export const SEGMENTATION_MODE = Object.freeze({
    RESTRICTED: 'restricted',
    WHITELIST: 'whitelist',
    OPEN: 'open',
    DISABLED: 'disabled',
    BLOCKED: 'blocked'
});

// Copy shown in `ConfigurationCard.description` and `NoticePanel` when the
// active tab is OPEN / DISABLED. Lives here so the wording stays consistent
// across the form, summary side-card, and any future surface that needs to
// describe each access mode.
export const MODE_DESCRIPTIONS = {
    [SEGMENTATION_MODE.RESTRICTED]:
        'Only users that match every enabled criterion above can access the service.',
    [SEGMENTATION_MODE.WHITELIST]:
        'Only the explicit list of member IDs below can access the service. Segmentation criteria are ignored.',
    [SEGMENTATION_MODE.OPEN]:
        'All users may access the service. Segmentation criteria are ignored while in this mode.',
    [SEGMENTATION_MODE.DISABLED]:
        'The service is currently disabled. No users can access it regardless of criteria.'
};


// Fully-baked items array consumed directly by `<Tabs items={...} />`. Each
// entry already carries its label, icon, and per-tab root class so the
// consumer (`ServiceAccessCard`) just spreads it in.
export const SEGMENTATION_MODE_TABS = [
    {
        key: SEGMENTATION_MODE.RESTRICTED,
        label: 'Restricted',
        icon: <SafetyOutlined className={userEligibilityFormStyles.iconRestricted} />,
        classNames: { root: userEligibilityFormStyles.modeTabRestricted, }
    },
    {
        key: SEGMENTATION_MODE.OPEN,
        label: 'Open',
        icon: <UnlockOutlined className={userEligibilityFormStyles.iconOpen} />,
        classNames: { root: userEligibilityFormStyles.modeTabOpen, }
    },
    {
        key: SEGMENTATION_MODE.DISABLED,
        label: 'Disabled',
        icon: <StopOutlined className={userEligibilityFormStyles.iconDisabled} />,
        classNames: { root: userEligibilityFormStyles.modeTabDisabled, }
    },
    {
        key: SEGMENTATION_MODE.WHITELIST,
        label: 'Whitelist',
        icon: <TeamOutlined className={userEligibilityFormStyles.iconWhitelist} />,
        classNames: { root: userEligibilityFormStyles.modeTabWhitelist, }
    },
    {
        key: 'blocked',
        label: 'Blocked',
        icon: <LockOutlined style={{ color: '#c5504b' }} />,
        classNames: { root: userEligibilityFormStyles.modeTabBlocked }
    },
];

export const FEE_INSTANCE_OPTIONS = [
    { value: 'on-advance', label: 'At Advance' },
    { value: 'on-repayment', label: 'At Settlement' },
];

export const FEE_STRATEGY_OPTIONS = [
    { value: 'on-top-of-advance', label: 'On top of advance' },
    { value: 'reduce-from-advance', label: 'Reduce from advance' },
];

export const STANDARD_DELIVERY_UNIT_OPTIONS = [
    { value: 'business-days', label: 'Business days' },
    { value: 'calendar-days', label: 'Calendar days' },
    { value: 'hours', label: 'Hours' },
];

export const PRODUCT_TYPE_OPTIONS = [
    { value: 'basic-checking', label: 'Basic Checking' },
    { value: 'premium-checking', label: 'Premium Checking' },
    { value: 'loan', label: 'Loan' },
    { value: 'credit-card', label: 'Credit Card' },
    { value: 'overdraft-protection', label: 'Overdraft Protection' },
];

export const FREE_ADVANCE_CONDITION_OPTIONS = [
    { value: 'first-advance', label: 'first advance' },
    { value: 'monthly', label: 'monthly' },
    { value: 'weekly', label: 'weekly' },
    { value: 'daily', label: 'daily' },
];

// --- Default state slices for tab controllers ----------------------------
//
// Keep the per-tab default state shapes here so the controllers stay focused
// on behaviour. These are the values each tab controller falls back to when
// the FI payload is missing / partial during initial load.

export const DEFAULT_SEGMENTATION_STATE = {
    segment_policy: {
        mode: SEGMENTATION_MODE.RESTRICTED,
        whitelist: [],
        blockOutOfSegmentation: false,
        restrictedCriteria: {
            tenure: { enabled: false, operator: 'gt', value: 90 },
            overdraft_instances: { enabled: false, operator: 'lt', value: 2, window_days: 30 },
            nsf_instances: { enabled: false, operator: 'lt', value: 2, window_days: 30 },
            negative_balance: { enabled: false, operator: 'lt', value: 5, window_days: 30 },
            employer_tenure: { enabled: false, operator: 'gt', value: 90 }
        }
    }
};

export const BALANCE_RULE_TYPE = {
    EXACTLY: 'exactly',
    DEDUCED: 'deduced'
};

export const DEFAULT_BALANCE_RULES = {
    userTenureRules: [{ id: 1, condition: '60-89-days', type: BALANCE_RULE_TYPE.EXACTLY, value: 50 }],
    overdraftRules: [],
    achRules: [],
    employmentTenureRules: [],
    employmentRules: [],
    balanceRules: []
};

export const DEFAULT_BALANCE_ENABLED = {
    userTenure: true,
    overdraft: true,
    ach: false,
    employmentTenure: false,
    employment: false,
    balance: false,
    doubleDipCap: true
};

export const DEFAULT_AVAILABLE_BALANCE_STATE = {
    threshold: 0.75,
    doubleDipCap: 0.5,
    balance_rules: DEFAULT_BALANCE_RULES,
    balance_enabled: DEFAULT_BALANCE_ENABLED
};

export const DEFAULT_FEE_POLICY = {
    standardFreeAdvanceTime: 1,
    standardFreeAdvanceUnit: 'business-days',
    feeInstance: 'on-advance',
    feeStrategy: 'on-top-of-advance'
};

export const DEFAULT_FEE_RULES = {
    volumeTierRules: [{ id: 1, fromAmount: 0, toAmount: 50, fee: 1.0 }],
    productTypeRules: [{ id: 1, productType: 'basic-checking', fee: 2.0 }],
    freeAdvanceRules: [{ id: 1, count: 1, condition: 'first-advance' }]
};

export const DEFAULT_FEE_ENABLED = {
    instantFee: true,
    volumeTiers: true,
    productTypes: true,
    freeAdvances: true
};

export const DEFAULT_FEE_STRUCTURE_STATE = {
    fee: 3.0,
    fee_policy: DEFAULT_FEE_POLICY,
    fee_rules: DEFAULT_FEE_RULES,
    fee_enabled: DEFAULT_FEE_ENABLED
};

export const DEFAULT_LIMIT_POLICY = {
    maxAdvances: 12,
    advanceTimeframe: 'pay-period',
    minAdvanceAmount: 5,
    maxAdvanceAmount: 700,
    maxAdvanceVolume: 1500,
    feeCap: 15,
    advanceCapTenureRules: [],
    volumeCapTenureRules: []
};

export const DEFAULT_LIMITS_ENABLED = {
    maxAdvances: true,
    advanceAmounts: true,
    totalVolume: true,
    feeCap: true
};

export const DEFAULT_ACCESS_RULES = {
    negativeBalanceDays: 10,
    overdraftNsfDays: 10,
    loansOverdueDays: 15
};

export const DEFAULT_ACCESS_ENABLED = {
    negativeBalance: true,
    overdraftNsf: true,
    loansOverdue: true
};

export const DEFAULT_LIMITS_STATE = {
    limit_policy: DEFAULT_LIMIT_POLICY,
    limits_enabled: DEFAULT_LIMITS_ENABLED,
    access_rules: DEFAULT_ACCESS_RULES,
    access_enabled: DEFAULT_ACCESS_ENABLED
};

// Mock figures used by the AvailableBalance side-card preview. Lives here
// so the controller's getSummary() stays focused on derivation.
export const PAYROLL_PREVIEW = { grossPay: 1238.96, payrollDeductions: 247.92 };

export const pageMetadata = {
    0: {
        title: { text: 'User', highlight: 'Criteria' },
        description: 'Define the specific criteria and thresholds for customer segments that will be permitted to access this financial service module.'
    },
    1: {
        title: { text: 'Available', highlight: 'Balance' },
        description: 'Define the specific criteria and thresholds for customer segments that will be permitted to access this financial service module.'
    },
    2: {
        title: { text: 'Fee', highlight: 'Structure' },
        description: 'Define the specific criteria and thresholds for customer segments that will be permitted to access this financial service module.'
    },
    3: {
        title: { text: 'Limits', highlight: 'Configuration' },
        description: 'Define the specific criteria and thresholds for customer segments that will be permitted to access this financial service module.'
    },
    4: {
        title: { text: 'Configuration', highlight: 'Complete' },
        description: 'Your configuration is ready.'
    }
};

export const USER_CRITERIA_DEFAULT_VALUE = {
    'tenure': { operator: 'gt', value: 90 },
    'overdraft_instances': { operator: 'lt', value: 2, window_days: 30 },
    'nsf_instances': { operator: 'lt', value: 2, window_days: 30 },
    'negative_balance': { operator: 'lt', value: 5, window_days: 30 },
    'employer_tenure': { operator: 'gt', value: 90 }
};